import React, { useState, useEffect } from 'react';
import { Sparkles, Twitter, Instagram, Facebook, Linkedin, Globe, Copy, Check, Clock, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import { getMe, generateContent } from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PLATFORMS = [
  { id: 'twitter', label: 'Twitter / X', icon: Twitter, color: '#1DA1F2', charLimit: '280 chars' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F', charLimit: 'Caption' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2', charLimit: 'Post' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2', charLimit: 'Article' },
  { id: 'general', label: 'General', icon: Globe, color: '#6B7280', charLimit: 'Any' },
];

const TONES = [
  { id: 'professional', label: 'Professional' },
  { id: 'casual', label: 'Casual & Friendly' },
  { id: 'inspirational', label: 'Inspirational' },
  { id: 'educational', label: 'Educational' },
  { id: 'urgent', label: 'Urgent / Awareness' },
];

export default function ContentCreationPage() {
  const [user, setUser] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('twitter');
  const [description, setDescription] = useState('');
  const [tone, setTone] = useState('professional');
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [includeEmojis, setIncludeEmojis] = useState(true);
  const [generatedContent, setGeneratedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await getMe();
        setUser(res.data);
      } catch { /* handled by ProtectedRoute */ }
    };
    loadUser();
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API_URL}/ai/content-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data);
    } catch { /* ignore */ }
  };

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error('Please describe the content you want');
      return;
    }
    setLoading(true);
    setGeneratedContent('');
    try {
      const res = await generateContent({
        platform: selectedPlatform,
        description: description.trim(),
        tone,
        include_hashtags: includeHashtags,
        include_emojis: includeEmojis
      });
      setGeneratedContent(res.data.content);
      loadHistory();
      toast.success('Content generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const activePlatform = PLATFORMS.find(p => p.id === selectedPlatform);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#4A90E2] to-[#2DD4BF] rounded-xl flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <h1 className="font-manrope font-bold text-3xl text-[#0F2A44]" data-testid="content-creation-title">
              Content Studio
            </h1>
          </div>
          <p className="font-public text-[#4B5563] ml-[52px]">
            AI-powered social media content for Afyagram
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Controls */}
          <div className="lg:col-span-2 space-y-5">
            {/* Platform Selector */}
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <label className="block text-sm font-semibold text-[#0F2A44] mb-3">Platform</label>
              <div className="grid grid-cols-5 gap-2">
                {PLATFORMS.map(p => {
                  const Icon = p.icon;
                  const isActive = selectedPlatform === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPlatform(p.id)}
                      data-testid={`platform-${p.id}`}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                        isActive ? 'border-[#0F2A44] bg-[#0F2A44]/5' : 'border-transparent hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="h-5 w-5" style={{ color: isActive ? p.color : '#9CA3AF' }} />
                      <span className={`text-[10px] font-medium ${isActive ? 'text-[#0F2A44]' : 'text-slate-400'}`}>
                        {p.label.split(' ')[0]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tone */}
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <label className="block text-sm font-semibold text-[#0F2A44] mb-3">Tone</label>
              <div className="flex flex-wrap gap-2">
                {TONES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setTone(t.id)}
                    data-testid={`tone-${t.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      tone === t.id
                        ? 'bg-[#0F2A44] text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Options */}
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <label className="block text-sm font-semibold text-[#0F2A44] mb-3">Options</label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={includeHashtags} onChange={e => setIncludeHashtags(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#4A90E2]" data-testid="hashtags-toggle" />
                  <span className="text-sm text-[#262626]">Include hashtags</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={includeEmojis} onChange={e => setIncludeEmojis(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#4A90E2]" data-testid="emojis-toggle" />
                  <span className="text-sm text-[#262626]">Include emojis</span>
                </label>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <label className="block text-sm font-semibold text-[#0F2A44] mb-3">
                Describe your content
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={`e.g. "Promote our new facility comparison feature that helps patients pick the right hospital" or "Health tip about malaria prevention during rainy season"`}
                rows={4}
                data-testid="content-description-input"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4A90E2]/20 focus:border-[#4A90E2] placeholder:text-slate-400"
              />
              <Button
                onClick={handleGenerate}
                disabled={loading || !description.trim()}
                data-testid="generate-content-btn"
                className="w-full mt-3 bg-[#0F2A44] hover:bg-[#0F2A44]/90 text-white rounded-lg h-11 font-semibold disabled:opacity-50"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" />Generate for {activePlatform?.label}</>
                )}
              </Button>
            </div>
          </div>

          {/* Right: Output + History */}
          <div className="lg:col-span-3 space-y-5">
            {/* Generated Content */}
            <div className="bg-white rounded-xl border border-slate-100 p-6 min-h-[280px]">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {activePlatform && <activePlatform.icon className="h-5 w-5" style={{ color: activePlatform.color }} />}
                  <h3 className="font-semibold text-[#0F2A44]">Generated Content</h3>
                  <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">{activePlatform?.charLimit}</span>
                </div>
                {generatedContent && (
                  <Button
                    onClick={handleCopy}
                    variant="ghost"
                    size="sm"
                    data-testid="copy-content-btn"
                    className="text-slate-500 hover:text-[#0F2A44]"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    <span className="ml-1.5 text-xs">{copied ? 'Copied' : 'Copy'}</span>
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <Loader2 className="h-8 w-8 animate-spin mb-3" />
                  <p className="text-sm">Crafting your content...</p>
                </div>
              ) : generatedContent ? (
                <div className="prose prose-sm max-w-none" data-testid="generated-content-output">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-[#262626] leading-relaxed bg-slate-50 p-4 rounded-lg border border-slate-100">
                    {generatedContent}
                  </pre>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-slate-300">
                  <Sparkles className="h-10 w-10 mb-3" />
                  <p className="text-sm text-slate-400">Your generated content will appear here</p>
                  <p className="text-xs text-slate-300 mt-1">Select a platform, describe what you need, and hit Generate</p>
                </div>
              )}
            </div>

            {/* History */}
            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <button
                onClick={() => setShowHistory(!showHistory)}
                data-testid="toggle-history-btn"
                className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold text-sm text-[#0F2A44]">Recent History</span>
                  <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">{history.length}</span>
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
              </button>

              {showHistory && (
                <div className="border-t border-slate-100 max-h-[400px] overflow-y-auto">
                  {history.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No content generated yet</p>
                  ) : (
                    history.map((item, idx) => {
                      const platform = PLATFORMS.find(p => p.id === item.platform);
                      const PIcon = platform?.icon || Globe;
                      return (
                        <div
                          key={item.content_id || idx}
                          className="p-4 border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => {
                            setGeneratedContent(item.generated_content);
                            setSelectedPlatform(item.platform);
                          }}
                          data-testid={`history-item-${idx}`}
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <PIcon className="h-3.5 w-3.5" style={{ color: platform?.color || '#6B7280' }} />
                            <span className="text-xs font-medium text-slate-500 capitalize">{item.platform}</span>
                            <span className="text-xs text-slate-300">
                              {new Date(item.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 line-clamp-2">{item.description}</p>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
